import { useCallback, useEffect, useState } from 'react';
import has from 'lodash/has';
import { fireIssueCreateErrorAnalytics as fireErrorAnalytics } from '@atlassian/jira-issue-create-analytics-common/src/utils/fire-error-analytics/index.tsx';
import { performPutRequest, performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';

const setGICUserPreferences = async <T,>(key: string, value: T) => {
	try {
		await performPutRequest(`/rest/api/3/mypreferences?key=${key}`, {
			body: JSON.stringify({ value }),
		});
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'getAPI3MyPreferences',
				packageName: 'jiraIssueCreate',
				teamName: 'gryffindor',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
	}
};

export const useUserPreferences = <T,>(key: string, fallbackState: T): [T, (value: T) => void] => {
	const [state, setState] = useState(fallbackState);
	useEffect(() => {
		const run = async () => {
			try {
				const apiResult = await performGetRequest(`/rest/api/3/mypreferences?key=${key}`);
				if (typeof apiResult !== 'object' || !has(apiResult, 'value')) {
					const error = new Error('Invalid preference value');
					fireErrorAnalytics({
						meta: {
							id: 'getAPI3MyPreferences',
							packageName: 'jiraIssueCreate',
							teamName: 'gryffindor',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					return;
				}
				setState(apiResult.value);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				if (error.statusCode === 404) {
					// Error 404 is acceptable as the first time the key does not exist in the user profile.
					return;
				}
				fireErrorAnalytics({
					meta: {
						id: 'getAPI3MyPreferences',
						packageName: 'jiraIssueCreate',
						teamName: 'gryffindor',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		};
		run();
	}, [key]);

	const setUserPreferences = useCallback(
		(value: T) => {
			setGICUserPreferences(key, value);
			setState(value);
		},
		[key],
	);

	return [state, setUserPreferences];
};
